export enum QuestionType {
  MULTIPLE_SELECTION = 1,
  NUMBER = 2,
  TEXT = 3,
  SINGLE_SELECTION = 4,
  SLIDER = 5,
}

const QUESTION_TYPE_TOKEN_MAP: Record<string, QuestionType> = {
  MULTIPLE_SELECTION: QuestionType.MULTIPLE_SELECTION,
  NUMBER: QuestionType.NUMBER,
  TEXT: QuestionType.TEXT,
  SINGLE_SELECTION: QuestionType.SINGLE_SELECTION,
  SLIDER: QuestionType.SLIDER,
};

const QUESTION_TYPE_VALUES = new Set<QuestionType>(
  Object.values(QUESTION_TYPE_TOKEN_MAP)
);

export const normalizeQuestionType = (
  value: unknown,
  fallback: QuestionType = QuestionType.TEXT
): QuestionType => {
  if (typeof value === "number" && QUESTION_TYPE_VALUES.has(value as QuestionType)) {
    return value as QuestionType;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return fallback;

    const numericValue = Number(trimmed);
    if (
      Number.isInteger(numericValue) &&
      QUESTION_TYPE_VALUES.has(numericValue as QuestionType)
    ) {
      return numericValue as QuestionType;
    }

    return QUESTION_TYPE_TOKEN_MAP[trimmed.toUpperCase()] ?? fallback;
  }

  return fallback;
};

export const normalizeQuestions = <T extends { type?: unknown }>(
  questions: T[] | null | undefined
): Array<Omit<T, "type"> & { type: QuestionType }> =>
  Array.isArray(questions)
    ? questions.map((question) => ({
        ...question,
        type: normalizeQuestionType(question?.type),
      }))
    : [];

export type InterstitialType =
  | "value"
  | "stat"
  | "testimonial"
  | "quote"
  | "credibility";

// =======================
// Shared mini-types
// =======================
export type Option = {
  description: string;
  value: string;
};

export type LoadingUI = {
  title: string;
  progress_label: string;
  alerts: string[];
};

export type BeforeAfter = {
  before: string[];
  after: string[];
};

export type Commit = {
  greeting: string;
  commitments: string[];
  subcopy: string;
  needSignature: boolean;
  primaryCta: string;
};

export type Review = {
  name: string;
  text: string;
  stars: 1 | 2 | 3 | 4 | 5;
  avatarUrl?: string;
};

export type Ready = {
  headline: string;
  bullets: string[];
  primaryCta: string;
};

// =======================
// Template goals
// =======================
export enum GoalType {
  SHORT_TERM = "short_term",
  MID_TERM = "mid_term",
  LONG_TERM = "long_term",
}

export type TemplateTask = {
  id: string;
  title: string;
  description: string;
  dateToBeDone: string;
  priority: number;
  completed: boolean;
};

type TemplateGoalBase = {
  id: string;
  title: string;
  description: string;
  category: string;
  createdAt: string;
  deadline: string;
  type: GoalType;
  icon: string;
  xpGained: number;
  color: string;
  tasks: TemplateTask[];
};

export type TemplateSubgoal = TemplateGoalBase & {
  parentId: string;
};

export type TemplateGoal = TemplateGoalBase & {
  subgoals: TemplateSubgoal[];
};

// =======================
// Interstitials (discriminated union)
// =======================
type InterstitialBase = {
  id: string;
  type: InterstitialType;
  afterQuestionId: string;
  headline: string;
  body: string;
  caption?: string;

  ctaLabel: string;
  backgroundUri?: string;
};

export type InterstitialValue = InterstitialBase & {
  type: "value";
};

export type InterstitialStat = InterstitialBase & {
  type: "stat";
};

export type InterstitialTestimonial = InterstitialBase & {
  type: "testimonial";
  review: string;
  reviewer: string;
};

export type InterstitialQuote = InterstitialBase & {
  type: "quote";
  author: string;
  // optionally allow a short user review as social proof on quote cards
  review?: string;
  reviewer?: string;
};

export type InterstitialCredibility = InterstitialBase & {
  type: "credibility";
  badges: string[];
};

export type Interstitial =
  | InterstitialValue
  | InterstitialStat
  | InterstitialTestimonial
  | InterstitialQuote
  | InterstitialCredibility;

// =======================
// Questions (safer per type)
// =======================
type BaseQuestion = {
  id: string;
  question: string;
  type: QuestionType;

  // optional UI helpers
  image?: string;
  rightAddon?: string;

  // gating/logic hooks (keep your existing number; add richer future-proofing)
  requires?: number; // legacy support
  requiresAllOf?: { questionId: string; valueIn?: string[] }[];
};

export type SingleSelectionQuestion = BaseQuestion & {
  type: QuestionType.SINGLE_SELECTION;
  options: Option[];
};

export type MultipleSelectionQuestion = BaseQuestion & {
  type: QuestionType.MULTIPLE_SELECTION;
  options: Option[];
};

export type NumberQuestion = BaseQuestion & {
  type: QuestionType.NUMBER;
  placeholder?: string;
  min?: number;
  max?: number;
  step?: number;
  unit?: string; // e.g. "hrs", "g", etc.
};

export type TextQuestion = BaseQuestion & {
  type: QuestionType.TEXT;
  placeholder?: string;
  multipleLines?: boolean;
  maxLength?: number;
};

export type SliderQuestion = BaseQuestion & {
  type: QuestionType.SLIDER;
  min: number;
  max: number;
  step?: number;
  unit?: string;
};

export type Question =
  | SingleSelectionQuestion
  | MultipleSelectionQuestion
  | NumberQuestion
  | TextQuestion
  | SliderQuestion;

// =======================
// Answers/result
// =======================
export type AnswerValue = string | number | string[];

export type Answer = {
  questionId: string;
  answer: AnswerValue;
};

export type QuestionnaireResult = {
  createdAt: string; // ISO
  answers: Answer[];
  userId: string;
};

export type LearningLevel = "beginner" | "intermediate" | "advanced";
export type LearningPlacementRouting = Partial<
  Record<LearningLevel, string>
>;
export type PlanRouting = Record<string, Record<string, string>>;

export type Questionnaire = {
  app: string;
  version: string;

  loading: LoadingUI;

  categories: string[];
  questions: Question[];
  interstitials: Interstitial[];

  beforeAfter: BeforeAfter;
  commit: Commit;
  reviews: Review[];
  ready: Ready;

  // prompt shown to the AI that generates paywall copy, etc.
  ai_prompt: string;

  // Optional/legacy fields you mentioned previously
  coachingType?: string;
  blockAI?: boolean;
  specificConsiderations?: string[];
  goalExamples?: string[];
  templateGoals?: TemplateGoal[];
  aiPrompt?: string; // legacy alias of `ai_prompt`
  planRouting?: PlanRouting;
  learningPlacementRouting?: LearningPlacementRouting;
};
